import React from 'react';
import PropTypes from 'prop-types';

/**
 * @typedef {import('./types').TagManagerActionsProps} TagManagerActionsProps
 */

/**
 * Renders the Tag Manager actions bar: the feature toggle, the new tag
 * input, and the add button.
 *
 * @param {TagManagerActionsProps} props - The component props.
 * @returns {JSX.Element} The actions bar markup.
 */
function TagManagerActions(props) {
  const { enabled, onToggleEnabled, configStatus, newTag, onNewTagChange, onAddTag, addDisabled } =
    props;
  const configLoaded = configStatus === 'loaded';
  const toggleDisabled = configStatus !== 'idle' && !configLoaded;

  return (
    <div
      data-test="tagmanager-actions"
      className="grid grid-cols-[repeat(4,_max-content)] md:grid-cols-[repeat(6,_max-content)] xl:grid-cols-[repeat(13,_max-content)] gap-2 mt-8"
    >
      <label
        className="flex items-center gap-2 cursor-pointer"
        data-test="tagmanager-toggle-label"
        htmlFor="tagmanager-toggle"
      >
        <span className="text-sm font-medium text-gray-700">Tag Manager</span>
        <button
          id="tagmanager-toggle"
          type="button"
          onClick={onToggleEnabled}
          disabled={toggleDisabled}
          data-test="tagmanager-toggle"
          style={{
            width: '50px',
            height: '26px',
            borderRadius: '9999px',
            position: 'relative',
            cursor: toggleDisabled ? 'not-allowed' : 'pointer',
            opacity: toggleDisabled ? 0.5 : 1,
            backgroundColor: enabled ? '#0a364b' : '#d1d5db',
            transition: 'all 300ms',
          }}
        >
          <span
            style={{
              position: 'absolute',
              top: '2px',
              left: enabled ? '26px' : '2px',
              width: '22px',
              height: '22px',
              backgroundColor: 'white',
              borderRadius: '9999px',
              transition: 'all 300ms',
            }}
          />
        </button>
      </label>
      {enabled && (
        <input
          type="text"
          value={newTag}
          onChange={e => onNewTagChange(e.target.value)}
          onKeyDown={e => {
            if (e.key === 'Enter' && !addDisabled) {
              onAddTag();
            }
          }}
          placeholder="New tag name..."
          data-test="tagmanager-newtag-input"
          className="w-[200px] h-[36px] px-3 rounded-lg border border-gray-300 text-sm"
        />
      )}
      {enabled && (
        <button
          type="button"
          onClick={onAddTag}
          disabled={addDisabled}
          data-test="tagmanager-newtag-submit"
          className="duration-500 hover:scale-105 w-[36px] cursor-pointer h-[36px] flex justify-center items-center bg-white rounded-lg"
        >
          <img
            src="/assets/images/icons/add_tag.svg"
            alt="Add tag"
            className="w-[20px] h-[20px]"
            draggable={false}
          />
        </button>
      )}
    </div>
  );
}

TagManagerActions.propTypes = {
  enabled: PropTypes.bool,
  onToggleEnabled: PropTypes.func.isRequired,
  configStatus: PropTypes.string,
  newTag: PropTypes.string,
  onNewTagChange: PropTypes.func.isRequired,
  onAddTag: PropTypes.func.isRequired,
  addDisabled: PropTypes.bool,
};

export default TagManagerActions;
